import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { RoleEnum, type Invitation, type Team } from 'api-client';

interface InvitationManagementProps {
  team: Team;
  pending: boolean;
  error: string | null;
  success: string | null;
  onSend: (email: string, role: RoleEnum) => Promise<boolean>;
  onResend: (invitation: Invitation) => void;
  onCancel: (invitation: Invitation) => Promise<boolean>;
}
export default function InvitationManagement({
  team,
  pending,
  error,
  success,
  onSend,
  onResend,
  onCancel,
}: InvitationManagementProps) {
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<RoleEnum>(RoleEnum.Member);
  const [cancellationStarted, setCancellationStarted] = useState<boolean>(false);
  const [cancel, setCancel] = useState<Invitation | null>(null);
  const invitations = team.invitations || [];
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending || !email.trim() || !team.isAdmin) return;
    if (await onSend(email.trim(), role)) {
      setEmail('');
      setRole(RoleEnum.Member);
    }
  };
  return (
    <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Typography component="h2" variant="h6">
        Team Invitations
      </Typography>
      {error && !cancel && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {team.isAdmin ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            component="form"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <Stack spacing={2}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                fullWidth
                disabled={pending}
                helperText="Enter the address to invite."
              />
              <FormControl fullWidth disabled={pending}>
                <InputLabel id="invitation-role-label">Invitation Role</InputLabel>
                <Select
                  labelId="invitation-role-label"
                  label="Invitation Role"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <MenuItem value={RoleEnum.Member}>Member</MenuItem>
                  <MenuItem value={RoleEnum.Admin}>Administrator</MenuItem>
                </Select>
              </FormControl>
              <Button
                type="submit"
                variant="contained"
                disabled={pending || !email.trim()}
                sx={{ alignSelf: 'flex-start' }}
                startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {pending ? 'Processing invitation…' : 'Send Invitation'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">You need administrator permissions to manage invitations.</Alert>
      )}
      <Typography component="h3" variant="subtitle1">
        Pending Invitations ({invitations.length})
      </Typography>
      {invitations.length ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          tabIndex={0}
          role="region"
          aria-label="Pending invitations"
          sx={{ minWidth: 0 }}
        >
          <Table aria-label="Pending team invitations">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell component="th" scope="row">
                    {invitation.email}
                  </TableCell>
                  <TableCell>
                    <Chip variant="outlined" label={invitation.role === RoleEnum.Admin ? 'Administrator' : 'Member'} />
                  </TableCell>
                  <TableCell>
                    {team.isAdmin && !invitation.isAccepted && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          disabled={pending}
                          onClick={() => onResend(invitation)}
                          aria-label={`Resend invitation to ${invitation.email}`}
                        >
                          Resend
                        </Button>
                        <Button
                          disabled={pending}
                          color="error"
                          onClick={() => {
                            setCancellationStarted(false);
                            setCancel(invitation);
                          }}
                          aria-label={`Cancel invitation to ${invitation.email}`}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary">No pending invitations.</Typography>
      )}
      <Dialog
        open={Boolean(cancel)}
        onClose={pending ? undefined : () => setCancel(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="cancel-invitation-title"
      >
        <DialogTitle id="cancel-invitation-title">Cancel Invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The invitation to {cancel?.email} will no longer work. A new invitation can be sent.
          </DialogContentText>
          {error && cancellationStarted && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => setCancel(null)}>
            Keep Invitation
          </Button>
          <Button
            disabled={pending}
            color="error"
            onClick={async () => {
              setCancellationStarted(true);
              if (cancel && (await onCancel(cancel))) setCancel(null);
            }}
          >
            {pending ? 'Cancelling…' : 'Cancel Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
